import React, { useState, useEffect, Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Row, Col } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TimePicker, Switch, Divider } from 'antd'
import moment from 'moment'
import {
  faInfoCircle,
  faSpinnerThird,
  faRotate,
} from '@fortawesome/pro-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'

import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { CourseStateType, ImgStatusEnum, CourseType } from 'enum'
import { getSelected } from 'helpers/react-select.helper'
import { ErrorToast, SuccessToast } from 'components/ui/toast/toastsTemplates'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import useIntegrations from 'hooks/useIntegrations'
import { ICourseModel } from 'models/course.model'
import { useHasSence } from 'store/selectors'
import LinkExternalCourse from 'api/courses/LinkExternalCourse'
import getDc3AttestationAssigned from 'api/administration/courses/GetDc3AttestationAssigned'
import { IMainState } from 'store/states'
import { RequiredFieldLabel } from 'components/reusables/requiredFieldLabel'
import { useTranslationRankmi } from 'helpers/translations.helper'
import CourseProfilesField from 'components/reusables/courseProfilesField'
import { FormInfoAlert } from 'components/reusables/formInfoAlert'
import { useAccess } from 'contexts/AccessContext'
import getInactivationStats from 'api/administration/courses/GetInactivationStats'
import RichTextInput from '../field/rich-text-input/RichTextInput'
import SelectInput from '../field/select-input/SelectInput'
import DatePickerInput from '../field/date-picker-input/DatePickerInput'
import CheckBoxRadioButtonInput from '../field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import InputLabel from '../field/input-label/InputLabel'
import { CreateNewCourseForm, OnSubmitValues } from './types'
import {
  CourseTypeInput,
  NotificationsInput,
  SynchronousCheckBox,
  TrainingTypeInput,
  CourseNameInput,
  EstimatedDurationInput,
  CourseImageFileInput,
} from './components/forms'
import { NormativeCertificateAssociationAlert } from './components/forms/NormativeCertificateAssociationAlert'
import { NormativeCourseEditAlert } from './components/forms/NormativeCourseEditAlert'
import { ConfirmNormativeCourseEditModal } from './components/forms/ConfirmNormativeCourseEditModal'
import { ConfirmInactivateCourseModal } from './components/forms/ConfirmInactivateCourseModal'
import { ConfirmInactiveToDraftModal } from './components/forms/ConfirmInactiveToDraftModal'
import { courseFormHasContentChanges } from './courseFormChanges'
import { CourseFormFooter } from './components/CourseFormFooter'
import { useForm, FormProvider, useFieldExternalIntegration } from './context'
import { defaultValues } from './common'
import { ShowDates } from './constants'
import { validate, imgFileValidate } from './validate'
import { Sence } from './components/forms/Sence'
import styles from './CourseForm.module.scss'
import EmailNotifications from './EmailNotifications'
import {
  ActionButtonsRow,
  CourseFormField,
  CourseImagesHeader,
  CourseImagesInfoIcon,
  CourseImagesList,
  CourseImagesSection,
  CourseImagesTitle,
  NormativeCertificateAlertWrapper,
  ClosedCourseRequestAlertWrapper,
  Subtitle,
  Wrapper,
  CourseFormFieldStyles,
  COURSE_FIELD_LABEL_CLASS,
} from './styled'
import { useStateOptions } from './hooks/useStateOptions'
import { useProfiles } from './hooks/useProfiles'

interface Params {
  courseId: string
}

interface Props {
  onSubmit: (values: OnSubmitValues, onlySave: boolean) => void
  initialValues?: ICourseModel
  initialNormative?: boolean
}

const CourseFormBase: React.FC<Props> = ({
  onSubmit,
  initialValues,
  initialNormative = false,
}) => {
  const { t } = useTranslation()
  const { access } = useAccess()
  const canManageProfiles = access.canAssignCourseProfiles()
  const canEditCourseContent = access.can('manageInternalCourses')
  const showProfilesSection = canEditCourseContent || canManageProfiles
  const tFields = useTranslationRankmi({
    prefix: 'administration.courses.new.form.courseFields',
  })
  const { courseId } = useParams<Params>()
  const history = useHistory()
  const { values, setValues, setFieldValue, errors, setErrors, valid } =
    useForm()
  const courseState = values.state?.value
  const isImparted = courseState === CourseStateType.Imparted
  const isInactive = courseState === CourseStateType.Inactive
  const isPublished = courseState === CourseStateType.Published
  const initialCourseState = initialValues?.state ?? CourseStateType.Draft

  const { description } = values
  const dispatch = useDispatch()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const [loading, setLoading] = useState(false)
  const [showNormativeConfirmModal, setShowNormativeConfirmModal] =
    useState(false)
  const [pendingOnlySave, setPendingOnlySave] = useState<boolean | null>(null)
  const [showInactiveConfirmModal, setShowInactiveConfirmModal] =
    useState(false)
  const [showInactiveToDraftModal, setShowInactiveToDraftModal] =
    useState(false)
  const [inactiveStats, setInactiveStats] = useState({
    inProgress: 0,
    notStarted: 0,
  })
  const [loadingInactiveStats, setLoadingInactiveStats] = useState(false)
  const [hasDc3Attestation, setHasDc3Attestation] = useState<boolean | null>(
    null
  )
  const [dc3AttestationName, setDc3AttestationName] = useState<string | null>(
    null
  )

  const [isLinking, setIsLinking] = useState(false)
  const { integrationCoursera } = useIntegrations()

  const setFields = React.useCallback(
    (values: Partial<CreateNewCourseForm>) => {
      setValues((current) => ({ ...current, ...values }))
    },
    [setValues]
  )

  const isSynchronous = values.courseSynchronous
  const externalIntegrationField = useFieldExternalIntegration()
  const externalIntegration = externalIntegrationField.value
  const hasExternalIntegration = !!externalIntegration?.state
  const isEdit = !!initialValues?.id
  const profilesOnlyEdit = isEdit && canManageProfiles && !canEditCourseContent
  const disableField = (extraDisabled = false) =>
    profilesOnlyEdit || extraDisabled
  const profilesFieldDisabled = !canManageProfiles || hasExternalIntegration

  const hasSence = useHasSence()
  const hasLessons = !!initialValues?.lessons.length
  const {
    stateOptions,
    stateOptionsBase,
    publishedErrors,
    draftOption,
    importedOption,
  } = useStateOptions({
    untilDate: values.untilDate,
    showDates: values.showDates,
    hasLessons,
    isSynchronous,
    hasExternalIntegration,
  })

  useEffect(() => {
    if (publishedErrors.length > 0 && isPublished)
      setFields({ state: draftOption })
  }, [setFields, publishedErrors, isPublished, draftOption])

  const { profiles } = useProfiles()

  useEffect(() => {
    if (initialValues) {
      const { startDate, untilDate, synchronousCourse } = initialValues
      const { maximumGrade, minimumGrade } = {
        ...synchronousCourse,
      }
      const { synchronousCourseSource } = { ...synchronousCourse }
      const courseType = initialValues.courseType || defaultValues.courseType

      const initial: CreateNewCourseForm = {
        ...initialValues,
        startDate: startDate ? moment(startDate).toDate() : undefined,
        untilDate: untilDate ? moment(untilDate).toDate() : undefined,
        state: getSelected(initialValues?.state || 0, stateOptionsBase, false),
        optionalProfiles:
          getSelected(initialValues.optionalProfiles, profiles, true) || [],
        requiredProfiles:
          getSelected(initialValues.requiredProfiles, profiles, true) || [],
        courseType,
        showDates:
          initialValues.startDate || initialValues.untilDate
            ? ShowDates.active
            : ShowDates.inactive,
        banner: initialValues.bannerData
          ? { name: initialValues.bannerData.metadata.filename }
          : undefined,
        thumbnail: initialValues.thumbnailData
          ? { name: initialValues.thumbnailData.metadata.filename }
          : undefined,
        canGenerateCertificate: initialValues.canGenerateCertificate
          ? initialValues.canGenerateCertificate
          : false,
        reminderNotification: initialValues.reminderNotification,
        reminderNoStartCourseNotification:
          initialValues.reminderNoStartCourseNotification,
        enrolCreatedNotification: initialValues.enrolCreatedNotification,
        courseExternal: synchronousCourseSource?.value,
        courseTypeSynchronous: synchronousCourse?.synchronousCourseType,
        courseSynchronousProperties: {
          min: String(minimumGrade || ''),
          max: String(maximumGrade || ''),
        },
        courseSynchronous: !!synchronousCourse,
        hours: initialValues.hours ?? '',
        integrations: {
          sence: initialValues.senceIntegration,
          external: {
            id: initialValues['integrationCourseId'],
            link: initialValues['integrationCourseLink'],
            state: !!initialValues['integrationCourseLink'],
          },
        },
        normative: initialValues.normative ?? false,
        canRestart: initialValues.canRestart ?? false,
      }
      setValues({
        ...defaultValues,
        ...initial,
        description: initialValues.description || '',
      })
    }
  }, [initialValues, profiles, setValues, stateOptionsBase])

  useEffect(() => {
    if (!initialValues && initialNormative && attestationsEnabled) {
      setFields({ normative: true })
    }
  }, [attestationsEnabled, initialNormative, initialValues, setFields])

  // TODO: Encontrar una mejor forma de limpiar las variables de error
  useEffect(() => {
    if (values.name !== '') setErrors((base) => ({ ...base, name: '' }))
  }, [setErrors, values.name])

  useEffect(() => {
    if (description !== '') setErrors((base) => ({ ...base, description: '' }))
  }, [description, setErrors])

  useEffect(() => {
    if (!attestationsEnabled || !values.normative) {
      setErrors((base) => ({ ...base, hours: '', name: '' }))
    }
  }, [attestationsEnabled, values.normative, setErrors])

  useEffect(() => {
    const hoursNum = Number(values.hours)
    if (values.hours !== '' && !Number.isNaN(hoursNum) && hoursNum > 0) {
      setErrors((base) => ({ ...base, hours: '' }))
    }
  }, [values.hours, setErrors])

  useEffect(() => {
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  useEffect(() => {
    if (!attestationsEnabled || !values.normative) {
      setHasDc3Attestation(null)
      setDc3AttestationName(null)
      return
    }

    if (!isEdit || !courseId) {
      setHasDc3Attestation(false)
      setDc3AttestationName(null)
      return
    }

    setHasDc3Attestation(null)
    setDc3AttestationName(null)

    let cancelled = false

    getDc3AttestationAssigned(Number(courseId))
      .then(({ assigned, name }) => {
        if (cancelled) return
        setHasDc3Attestation(assigned)
        setDc3AttestationName(assigned && name ? name : null)
      })
      .catch(() => {
        if (!cancelled) {
          setHasDc3Attestation(false)
          setDc3AttestationName(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [attestationsEnabled, isEdit, values.normative, courseId])

  useEffect(() => {
    return () => {
      setLoading(false)
    }
  }, [])

  const isActiveCoursera = useMemo(() => {
    const config = integrationCoursera?.config
    return !!(config?.appKey && config?.appSecret && config?.orgId)
  }, [integrationCoursera])

  const disabledSave = loading || !valid

  const onImgUpload = (file: any, origin: string) => {
    if (file) {
      imgFileValidate(file).then((response) => {
        response !== ImgStatusEnum.success
          ? ErrorToast(
              t(`administration.courses.new.form.validations.image.${response}`)
            )
          : setValues({ ...values, [origin]: file })
      })
    } else {
      setValues({ ...values, [origin]: '_destroy' })
    }
  }
  const hideDates = values.showDates === ShowDates.inactive

  const reminderNotification = React.useMemo(
    () => values.reminderNotification,
    [values.reminderNotification]
  )

  const validateForm = (): boolean => {
    let newErrors: any = validate({
      ...values,
      description,
      normative: values.normative,
      hours: values.hours,
      attestationsEnabled,
    })
    if (hasExternalIntegration && !externalIntegration?.link) {
      newErrors = {
        ...newErrors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.urlRequired'
        ),
      }
    }
    if (hasExternalIntegration && !externalIntegration.id) {
      newErrors = {
        ...newErrors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.linkUrlRequired'
        ),
      }
    }
    setErrors(newErrors)

    for (const field in newErrors) {
      // @ts-ignore
      if (newErrors[field] !== '') {
        return false
      }
    }

    return true
  }

  const runSubmit = (onlySave: boolean, stateOverride?: CourseStateType) => {
    setLoading(true)
    let dataToSend: CreateNewCourseForm = {
      ...values,
      description,
      reminderNotification,
    }
    if (stateOverride !== undefined) {
      dataToSend = {
        ...dataToSend,
        state: stateOptionsBase.find((o) => o.value === stateOverride) ?? {
          value: stateOverride,
          label: '',
        },
      }
    }
    dataToSend = hideDates
      ? { ...dataToSend, startDate: undefined, untilDate: undefined }
      : dataToSend
    onSubmit(dataToSend, onlySave)
    onlySave && SuccessToast(t('administration.toastMessages.editSuccess'))
  }

  const handleCloseNormativeConfirmModal = () => {
    setShowNormativeConfirmModal(false)
    setPendingOnlySave(null)
  }

  const isTransitionToInactive =
    isEdit &&
    courseState === CourseStateType.Inactive &&
    initialCourseState !== CourseStateType.Inactive

  const isInactiveContentEdit =
    isEdit &&
    initialCourseState === CourseStateType.Inactive &&
    courseState === CourseStateType.Inactive &&
    courseFormHasContentChanges(values, initialValues, description)

  const promptInactiveTransitionModal = async (onlySave: boolean) => {
    if (!isTransitionToInactive || !courseId) {
      return false
    }

    setPendingOnlySave(onlySave)
    setLoadingInactiveStats(true)
    try {
      const stats = await getInactivationStats(Number(courseId))
      setInactiveStats(stats)
      setShowInactiveConfirmModal(true)
      return true
    } catch {
      ErrorToast(
        t('administration.courses.edit.confirmInactiveModal.loadError')
      )
      return true
    } finally {
      setLoadingInactiveStats(false)
    }
  }

  const promptInactiveToDraftModal = (onlySave: boolean) => {
    if (!isInactiveContentEdit) {
      return false
    }

    setPendingOnlySave(onlySave)
    setShowInactiveToDraftModal(true)
    return true
  }

  const continueSaveFlow = async (onlySave: boolean) => {
    if (await promptInactiveTransitionModal(onlySave)) {
      return
    }
    if (promptInactiveToDraftModal(onlySave)) {
      return
    }
    runSubmit(onlySave)
  }

  const handleSaveAttempt = async (onlySave = false) => {
    if (!validateForm()) {
      return
    }

    if (
      attestationsEnabled &&
      isEdit &&
      values.normative &&
      !profilesOnlyEdit
    ) {
      setPendingOnlySave(onlySave)
      setShowNormativeConfirmModal(true)
      return
    }

    await continueSaveFlow(onlySave)
  }

  const handleConfirmInactivate = () => {
    if (pendingOnlySave === null) return
    const onlySave = pendingOnlySave
    setShowInactiveConfirmModal(false)
    setPendingOnlySave(null)
    runSubmit(onlySave)
  }

  const handleConfirmInactiveToDraft = () => {
    if (pendingOnlySave === null) return
    const onlySave = pendingOnlySave
    setShowInactiveToDraftModal(false)
    setPendingOnlySave(null)
    runSubmit(onlySave, CourseStateType.Draft)
  }

  const handleConfirmNormativeUpdate = async () => {
    if (!validateForm() || pendingOnlySave === null) {
      return
    }

    const onlySave = pendingOnlySave
    setShowNormativeConfirmModal(false)
    setPendingOnlySave(null)
    await continueSaveFlow(onlySave)
  }

  const joditConfig = {
    buttons: 'bold,italic,underline,strikethrough,|,image,link',
    statusbar: false,
    height: 120,
    minHeight: 120,
    style: {
      font: '14px Roboto, sans-serif',
      color: '#363636',
      lineHeight: '24px',
    },
  }

  const handlePreview = () => {
    window.open(`/administration/courses/${courseId}/preview`)
  }

  const handleLinkExternalCourse = () => {
    const url = externalIntegration?.link

    if (!url) {
      setErrors({
        ...errors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.urlRequired'
        ),
      })
      return
    }

    const validUrl = /^(ftp|http|https):\/\/[^ "]+$/.test(url)
    if (!validUrl) {
      setErrors({
        ...errors,
        integrationsExternalLink: t(
          'administration.courses.new.form.linkExternalCourse.errors.invalidUrl'
        ),
      })
      return
    }
    setIsLinking(true)
    LinkExternalCourse(url)
      .then(({ data }) => {
        setValues({
          ...values,
          integrations: {
            ...values.integrations,
            external: {
              state: true,
              id: data.id,
              link: url,
            },
          },
          name: data.name,
          description: data.description,
          hours: data.hours,
        })
        SuccessToast(
          t('administration.courses.new.form.linkExternalCourse.success')
        )
      })
      .catch(({ response }) => {
        switch (response.data.error_message) {
          case 'linked to another course': {
            const translation = t(
              'administration.courses.new.form.linkExternalCourse.errors.linkedToAnotherCourseV3'
            )
            ErrorToast(
              translation.replace(
                '{courseName}',
                `"${response.data.course_name}"`
              )
            )
            break
          }
          default:
            ErrorToast(
              t(
                'administration.courses.new.form.linkExternalCourse.errors.default'
              )
            )
            break
        }
      })
      .finally(() => setIsLinking(false))
  }

  const changeCourseType = (value: boolean) => {
    externalIntegrationField.change({
      ...externalIntegration,
      state: value,
      link: value ? externalIntegration?.link : '',
    })
    if (!value) setErrors((base) => ({ ...base, integrationsExternalLink: '' }))
  }

  return (
    <Wrapper>
      <Row className={`d-flex ${styles.formColumns}`}>
        <Col
          xs={12}
          className={`p-0 ${styles.noMargin} ${styles.principalCol}`}
        >
          {integrationCoursera?.active && isActiveCoursera && (
            <>
              <Form.Group>
                <div
                  className={`d-flex justify-content-between ${
                    hasExternalIntegration && styles.linkExternalSwitch
                  }`}
                >
                  <InputLabel
                    label={
                      <>
                        {t(
                          'administration.courses.new.form.linkExternalCourse.label'
                        )}
                        <CommonToolTip
                          text={t(
                            'administration.courses.new.form.linkExternalCourse.tooltip'
                          )}
                          position="right"
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className={`ml-2 ${styles.infoIcon}`}
                          />
                        </CommonToolTip>
                      </>
                    }
                  />
                  <Switch
                    className={styles.certificateSwitchBtn}
                    checked={hasExternalIntegration}
                    checkedChildren={t('administration.courses.new.form.yes')}
                    unCheckedChildren={t('administration.courses.new.form.no')}
                    onChange={(checked: boolean) => changeCourseType(checked)}
                    disabled={
                      profilesOnlyEdit ||
                      initialValues?.state === CourseStateType.Published ||
                      isLinking ||
                      (initialValues?.lessons.length || 0) > 0
                    }
                  />
                </div>
              </Form.Group>
            </>
          )}
          {hasExternalIntegration &&
            integrationCoursera?.active &&
            isActiveCoursera && (
              <Fragment>
                <Form.Group>
                  <TextInput
                    name={'integrationsExternalLink'}
                    value={externalIntegration.link}
                    label={t(
                      'administration.courses.new.form.linkExternalCourse.urlLabel'
                    )}
                    onChange={(e) => {
                      externalIntegrationField.change({
                        ...externalIntegration,
                        link: e.target.value,
                      })
                    }}
                    error={errors.integrationsExternalLink}
                    className="mb-2"
                    disabled={disableField(isEdit)}
                  />
                </Form.Group>
                <Form.Group>
                  <button
                    className={styles.linkExternalCourse}
                    disabled={
                      profilesOnlyEdit ||
                      initialValues?.state === CourseStateType.Published ||
                      isLinking
                    }
                    onClick={handleLinkExternalCourse}
                  >
                    <FontAwesomeIcon
                      icon={isLinking ? faSpinnerThird : faRotate}
                      spin={isLinking}
                    />
                    {t(
                      'administration.courses.new.form.linkExternalCourse.buttonLabel'
                    )}
                  </button>
                </Form.Group>
              </Fragment>
            )}
          {integrationCoursera?.active && isActiveCoursera && (
            <Divider className={styles.linkExternalDivider}></Divider>
          )}
          {attestationsEnabled ? (
            <TrainingTypeInput
              value={values.normative}
              onChange={(normative) => setFields({ normative })}
              disabled={disableField(hasExternalIntegration)}
            />
          ) : null}
          <CourseFormField>
            <CourseNameInput
              value={values.name}
              onChange={(name) => setFieldValue('name', name)}
              error={errors.name}
              disabled={disableField(hasExternalIntegration)}
              normative={attestationsEnabled && values.normative}
            />
          </CourseFormField>
          <CourseFormField>
            <RichTextInput
              name="description"
              value={description}
              label={
                <RequiredFieldLabel required>
                  {tFields('description.label')}
                </RequiredFieldLabel>
              }
              labelClassName={COURSE_FIELD_LABEL_CLASS}
              onChange={(value: any) => {
                setFieldValue('description', value)
              }}
              config={{
                ...joditConfig,
                placeholder: tFields('description.placeholder'),
                showPlaceholder: true,
                disablePlugins: '',
              }}
              error={errors['description']}
              disabled={disableField(hasExternalIntegration)}
            />
          </CourseFormField>
          <CourseFormField>
            <EstimatedDurationInput
              value={values.hours ?? ''}
              onChange={(hours) => setFieldValue('hours', hours)}
              error={errors.hours}
              required={attestationsEnabled && values.normative}
              disabled={disableField(hasExternalIntegration)}
            />
          </CourseFormField>
          <CourseImagesSection>
            <CourseImagesHeader>
              <CourseImagesTitle>
                {t('administration.courses.new.form.images.label')}
              </CourseImagesTitle>
              <CommonToolTip
                text={t('administration.courses.tooltips.courseImage')}
                position="right"
              >
                <CourseImagesInfoIcon>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </CourseImagesInfoIcon>
              </CommonToolTip>
            </CourseImagesHeader>
            <CourseImagesList>
              <CourseImageFileInput
                name="banner"
                label={t('administration.courses.new.form.images.banner')}
                hint={t(
                  'administration.courses.new.form.images.BannerSuggestedSize'
                )}
                value={values.banner}
                onChange={(file) => onImgUpload(file, 'banner')}
                error={errors.banner}
                disabled={disableField(hasExternalIntegration)}
              />
              <CourseImageFileInput
                name="thumbnail"
                label={t('administration.courses.new.form.images.thumbnail')}
                hint={t(
                  'administration.courses.new.form.images.ThumbnailSuggestedSizeNew'
                )}
                value={values.thumbnail}
                onChange={(file) => onImgUpload(file, 'thumbnail')}
                error={errors.thumbnail}
                disabled={disableField(hasExternalIntegration)}
              />
            </CourseImagesList>
          </CourseImagesSection>
          <Form.Group>
            <InputLabel
              label={
                <Subtitle>
                  {t('administration.courses.new.form.courseType.label')}
                </Subtitle>
              }
            />
          </Form.Group>
          <CourseTypeInput
            value={values.courseType}
            onChange={(newValue) => {
              const newValues: Partial<CreateNewCourseForm> = {
                courseType: newValue,
              }

              setFields(newValues)
            }}
            error={errors.courseType}
            disabled={disableField(hasExternalIntegration)}
          />
          {values['courseType'] === CourseType.Closed && (
            <ClosedCourseRequestAlertWrapper>
              <FormInfoAlert>
                {t('administration.courses.new.form.closedCourseRequestInfo', {
                  permission: t(
                    'configuration.rolesAndPermissions.form.sections.accesses.manageAdministratorRequests.title'
                  ),
                  defaultValue:
                    'La solicitud puede ser aprobada por cualquier usuario con el permiso «Gestionar solicitudes de administrador».',
                })}
              </FormInfoAlert>
            </ClosedCourseRequestAlertWrapper>
          )}
          <SynchronousCheckBox
            courseId={courseId}
            disabled={disableField(
              hasExternalIntegration || courseId?.length > 0
            )}
          />
          {hasSence && (
            <Sence disabled={disableField(hasExternalIntegration)} />
          )}
        </Col>
        <Col xs={12} className={`px-0 ${styles.principalCol}`}>
          <Form.Group className="m-0">
            <InputLabel
              label={
                <>
                  {t('administration.courses.new.form.dates.courseDates')}
                  <CommonToolTip
                    text={t('administration.courses.tooltips.courseDates')}
                    position="right"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={`ml-2 ${styles.infoIcon}`}
                    />
                  </CommonToolTip>
                </>
              }
            />
          </Form.Group>
          <Form.Group className="m-0">
            <CheckBoxRadioButtonInput
              name={'showDates-0'}
              value={ShowDates.active}
              checked={
                hasExternalIntegration
                  ? false
                  : values['showDates'] === ShowDates.active
              }
              inline={true}
              type={'radio'}
              label={t('administration.courses.new.form.dates.withDates')}
              onChange={() =>
                setValues({ ...values, showDates: ShowDates.active })
              }
              error={''}
              disabled={disableField(hasExternalIntegration)}
            />
            <CheckBoxRadioButtonInput
              name={'showDates-1'}
              value={ShowDates.inactive}
              checked={
                hasExternalIntegration
                  ? false
                  : values['showDates'] === ShowDates.inactive
              }
              inline={true}
              type={'radio'}
              label={t('administration.courses.new.form.dates.withoutDates')}
              onChange={() => {
                if (isImparted) {
                  setValues({
                    ...values,
                    state: draftOption,
                    showDates: ShowDates.inactive,
                    reminderNotification: undefined,
                  })
                  return
                }
                setValues({
                  ...values,
                  showDates: ShowDates.inactive,
                  reminderNotification: undefined,
                })
              }}
              error={''}
              disabled={disableField(hasExternalIntegration)}
            />
          </Form.Group>
          {!hideDates && (
            <Row className="m-0 mt-2" noGutters>
              <Form.Group as={Col} xs={6} className={'mb-0 pr-1'}>
                <DatePickerInput
                  name={'startDate'}
                  value={values['startDate']}
                  maxDate={values['untilDate']}
                  label={t('administration.courses.new.form.dates.inital')}
                  onChange={(date: any) => {
                    setValues({ ...values, startDate: date })
                  }}
                  error={errors['startDate']}
                  popperClassName={styles.datePicker}
                  disabled={disableField(hasExternalIntegration)}
                />
              </Form.Group>
              <Form.Group as={Col} xs={6} className={'mb-0 pl-1'}>
                <InputLabel
                  label={t('administration.courses.new.form.dates.hour')}
                />
                <TimePicker
                  format="HH:mm"
                  className={styles.timePicker}
                  onChange={(time) => {
                    setFieldValue('startDate', time?.toDate())
                  }}
                  value={
                    (values.startDate && moment(values.startDate)) || undefined
                  }
                  allowClear={false}
                  placeholder="HH:mm"
                  disabled={disableField(hasExternalIntegration)}
                />
              </Form.Group>
              <Form.Group as={Col} xs={12} className={'mb-0 mt-2'}>
                <DatePickerInput
                  name={'untilDate'}
                  value={values['untilDate']}
                  label={t('administration.courses.new.form.dates.close')}
                  minDate={values['startDate']}
                  onChange={(date) => {
                    if (
                      isImparted &&
                      values.untilDate &&
                      date > values.untilDate &&
                      isImparted
                    ) {
                      setValues({
                        ...values,
                        state: draftOption,
                        untilDate: date,
                      })
                      return
                    }
                    if (isPublished && date < new Date()) {
                      setValues({
                        ...values,
                        state: importedOption,
                        untilDate: date,
                      })
                      return
                    }
                    setValues({ ...values, untilDate: date })
                  }}
                  error={errors['untilDate']}
                  popperClassName={styles.datePicker}
                  disabled={disableField(hasExternalIntegration)}
                />
              </Form.Group>
            </Row>
          )}
          {isSynchronous && (
            <NotificationsInput
              config={reminderNotification?.config}
              active={!!reminderNotification?.active}
              onChange={(value) => {
                setFieldValue('reminderNotification', {
                  active: !!value,
                  emailBody: reminderNotification?.emailBody || '',
                  config: value ? { delayTime: value.reminder } : undefined,
                })
              }}
              error={errors.courseType}
              className="mt-2"
              disabled={disableField(hideDates || hasExternalIntegration)}
            />
          )}
          {showProfilesSection ? (
            <Form.Group className={styles.profilesFieldGroup}>
              <CourseProfilesField
                courseId={initialValues?.id}
                requiredProfiles={values['requiredProfiles'] || []}
                optionalProfiles={values['optionalProfiles'] || []}
                onChange={({ requiredProfiles, optionalProfiles }) =>
                  setValues({
                    ...values,
                    requiredProfiles,
                    optionalProfiles,
                  })
                }
                requiredError={errors['requiredProfiles']}
                optionalError={errors['optionalProfiles']}
                disabled={profilesFieldDisabled}
              />
            </Form.Group>
          ) : null}
          <Form.Group className={styles.certificateFormGroup}>
            <div className="d-flex align-items-center mb-1">
              <InputLabel
                className="mb-0"
                label={
                  <>
                    {t('administration.courses.new.form.certificates.title')}
                    <CommonToolTip
                      text={t(
                        'administration.courses.tooltips.courseCertificated'
                      )}
                      position="right"
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className={`ml-2 ${styles.infoIcon}`}
                      />
                    </CommonToolTip>
                  </>
                }
              />
              <Switch
                className={styles.certificateSwitchBtn}
                checked={values['canGenerateCertificate']}
                checkedChildren={t('administration.courses.new.form.yes')}
                unCheckedChildren={t('administration.courses.new.form.no')}
                onChange={(checked: boolean) =>
                  setValues({
                    ...values,
                    canGenerateCertificate: checked,
                  })
                }
                disabled={profilesOnlyEdit}
              />
            </div>
            <span className={styles.certificateMsg}>
              {values['canGenerateCertificate']
                ? initialValues?.individualCertificatesName
                  ? t(
                      'administration.courses.new.form.certificates.individual',
                      {
                        name: initialValues?.individualCertificatesName,
                        count: initialValues?.grupalCertificatesCount,
                      }
                    )
                  : t('administration.courses.new.form.certificates.create')
                : t('administration.courses.new.form.certificates.grupal', {
                    count: initialValues?.grupalCertificatesCount,
                  })}
            </span>
          </Form.Group>
          <Form.Group className={'mb-0'}>
            <div className="row no-gutters">
              <div className="col">
                <SelectInput
                  name="state"
                  label={t('administration.courses.new.form.state')}
                  placeHolder={t(
                    'administration.courses.new.form.placeholders.state'
                  )}
                  options={stateOptions}
                  closeMenuOnSelect={true}
                  value={values.state}
                  isMulti={false}
                  withColors={false}
                  onChange={(selected) =>
                    setValues({ ...values, state: selected })
                  }
                  error={errors['state']}
                  disabled={profilesOnlyEdit}
                  disabledReason={(option) => {
                    if (!option.disabled) return null

                    if (option.value === CourseStateType.Imparted) return null

                    if (option.value === CourseStateType.Published) {
                      const [publishedError] = publishedErrors
                      if (publishedError) return `(${publishedError})`
                    }
                  }}
                />
              </div>
              {courseState === CourseStateType.Soon &&
                (!values.startDate || hideDates) && (
                  <div className="col-auto d-flex align-items-center">
                    <div className="mt-4">
                      <CommonToolTip
                        text={t(
                          'administration.courses.tooltips.soonWithOutDate'
                        )}
                        position="left"
                        className={styles.soonWithOutDateTooltip}
                      >
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className={`ml-2 ${styles.infoIcon} color-warning`}
                        />
                      </CommonToolTip>
                    </div>
                  </div>
                )}
            </div>
          </Form.Group>
          <EmailNotifications
            isNewCourse={!initialValues}
            disableReminder={profilesOnlyEdit}
          />
          {attestationsEnabled &&
          values.normative &&
          isEdit &&
          hasDc3Attestation === true &&
          dc3AttestationName ? (
            <NormativeCertificateAlertWrapper>
              <NormativeCourseEditAlert certificateName={dc3AttestationName} />
            </NormativeCertificateAlertWrapper>
          ) : null}
          {attestationsEnabled &&
          values.normative &&
          (!isEdit || (isEdit && hasDc3Attestation === false)) ? (
            <NormativeCertificateAlertWrapper>
              <NormativeCertificateAssociationAlert />
            </NormativeCertificateAlertWrapper>
          ) : null}
        </Col>
      </Row>
      <ActionButtonsRow>
        <Col className="p-0">
          <CourseFormFooter
            loading={loading}
            disabledSave={disabledSave}
            showGoToLessons={!isSynchronous && !profilesOnlyEdit}
            showPreview={!!courseId && !profilesOnlyEdit}
            disableGoToLessons={hasExternalIntegration}
            onCancel={() => history.push('/administration/courses/')}
            onSaveAndExit={() => handleSaveAttempt(true)}
            onGoToLessons={() => handleSaveAttempt(false)}
            onPreview={handlePreview}
          />
        </Col>
      </ActionButtonsRow>
      <CourseFormFieldStyles />
      {attestationsEnabled ? (
        <ConfirmNormativeCourseEditModal
          isOpen={showNormativeConfirmModal}
          processing={loading}
          onClose={handleCloseNormativeConfirmModal}
          onConfirmAndUpdate={handleConfirmNormativeUpdate}
        />
      ) : null}
      <ConfirmInactivateCourseModal
        isOpen={showInactiveConfirmModal}
        processing={loading || loadingInactiveStats}
        inProgress={inactiveStats.inProgress}
        notStarted={inactiveStats.notStarted}
        onClose={() => {
          setShowInactiveConfirmModal(false)
          setPendingOnlySave(null)
        }}
        onConfirm={handleConfirmInactivate}
      />
      <ConfirmInactiveToDraftModal
        isOpen={showInactiveToDraftModal}
        processing={loading}
        onClose={() => {
          setShowInactiveToDraftModal(false)
          setPendingOnlySave(null)
        }}
        onConfirm={handleConfirmInactiveToDraft}
      />
    </Wrapper>
  )
}

const CourseForm: React.FC<Props> = (props) => {
  return (
    <FormProvider>
      <CourseFormBase {...props} />
    </FormProvider>
  )
}

export default CourseForm
